import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Receipt, Landmark, BarChart3, LogOut, Globe, Menu, X, Zap, ChevronRight, FolderOpen, Building2, ShoppingBag, Info, Shield, User, Brain, History } from 'lucide-react';
import BusinessSwitcher from './BusinessSwitcher';
import NotificationBell from './NotificationBell';
import ChatbotWidget from './ChatbotWidget';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, currentBusiness, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleLanguage = () => { i18n.changeLanguage(i18n.language === 'sw' ? 'en' : 'sw'); };
  const handleLogout = () => { logout(); navigate('/login'); };
  const isAdmin = user?.role === 'admin' || user?.role === 'ADMIN';

  const menuItems = [
    { icon: LayoutDashboard, label: t('dashboard'), path: '/' },
    { icon: FolderOpen, label: 'Categories', path: '/categories' },
    { icon: Package, label: t('products'), path: '/products' },
    { icon: ShoppingBag, label: 'Purchases', path: '/purchases' },
    { icon: ShoppingCart, label: t('sales'), path: '/sales' },
    { icon: Receipt, label: t('expenses'), path: '/expenses' },
    { icon: Landmark, label: t('loans'), path: '/loans' },
    { icon: Building2, label: 'Assets', path: '/assets' },
    { icon: BarChart3, label: t('reports'), path: '/reports' },
    { icon: Brain, label: "AI Insights", path: "/ai-insights" },
    { icon: History, label: 'History', path: '/history' },
    { icon: Info, label: 'About', path: '/about' },
  ];
  if (isAdmin) menuItems.push({ icon: Shield, label: 'Admin', path: '/admin' });

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="flex h-screen bg-[#0a0f0d] overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/70 z-40" onClick={() => setSidebarOpen(false)} />}
      
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static z-50 w-64 h-full bg-[#0d1410] border-r border-white/5 transition-transform duration-300 flex flex-col`}>
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center"><Zap size={18} className="text-white" fill="white" /></div>
            <span className="text-white font-bold text-sm">Interlink</span>
          </div>
          <div className="mt-3 px-3 py-2 bg-white/5 rounded-lg border border-white/5">
            <p className="text-white text-xs font-medium truncate">{currentBusiness?.businessName || user?.shopName || 'Shop'}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {menuItems.map((item) => (
            <button key={item.path} onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              className={`flex items-center gap-3 w-full p-2.5 rounded-lg transition text-sm ${isActive(item.path) ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-400 hover:text-white'}`}>
              <item.icon size={18} /><span className="text-xs">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-1">
          <button onClick={() => navigate('/profile')} className="flex items-center gap-3 w-full p-2.5 rounded-lg text-gray-400 hover:text-white text-xs"><User size={18} /> Profile</button>
          <button onClick={toggleLanguage} className="flex items-center gap-3 w-full p-2.5 rounded-lg text-gray-400 hover:text-white text-xs"><Globe size={18} /> {i18n.language === 'sw' ? 'English' : 'Kiswahili'}</button>
          <button onClick={handleLogout} className="flex items-center gap-3 w-full p-2.5 rounded-lg text-red-400 hover:bg-red-500/10 text-xs"><LogOut size={18} /> {t('logout')}</button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-[#0a0f0d] border-b border-white/5 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1.5 text-gray-400" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
            <h1 className="text-white text-base font-semibold">{menuItems.find(m => isActive(m.path))?.label || 'Dashboard'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <BusinessSwitcher />
            <div onClick={() => navigate('/profile')} className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-pointer">{user?.shopName?.charAt(0) || 'S'}</div>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6">
          {children}
        </div>
      </div>
      <ChatbotWidget />
    </div>
  );
};

export default Layout;
